import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import { Input } from "@components/ui/input"
import { Button } from "@components/ui/button"
import { Loader2 } from "lucide-react"
import { DataTable } from "@components/DataTable/DataTable"
import {
  useGetProspectPhoneNumbersQuery,
  useSetProspectPhoneNumberMutation,
} from "@apis/prospects/useProspects"
import type { ISetProspectPhoneNumberRequest } from "@apis/prospects/types"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select"
import { useGetPhoneNumberTypesQuery } from "@apis/shared/useShared"
import { prospectsKeys } from "@apis/prospects/queryKeys"
import { prospectPhoneNumbersColumns } from "./utils/tableColumnsSchema"

type TAccountPhoneNumberFormProps = {
  prospect_guid: string
  account_id: string
}

export const AccountPhoneNumberForm = ({
  prospect_guid,
  account_id,
}: TAccountPhoneNumberFormProps) => {
  const queryClient = useQueryClient()

  const getProspectPhoneNumbersQuery =
    useGetProspectPhoneNumbersQuery(prospect_guid)
  const getPhoneNumberTypesQuery = useGetPhoneNumberTypesQuery()
  const setProspectPhoneNumberMutation = useSetProspectPhoneNumberMutation()

  const form = useForm<ISetProspectPhoneNumberRequest>({
    mode: "onTouched",
    defaultValues: {
      phone: "",
      phone_type: "",
    },
  })

  const handleSubmit = (data: ISetProspectPhoneNumberRequest) => {
    setProspectPhoneNumberMutation.mutate(
      {
        prospect_guid,
        request: data,
      },
      {
        onSuccess: () => {
          toast.success("Successfully added phone number!")
          queryClient.invalidateQueries({
            queryKey: prospectsKeys.prospectPhoneNumbers(prospect_guid),
          })
          form.reset()
        },
        onError: (error) => toast.error(`Error adding phone number! ${error}`),
      },
    )
  }

  return (
    <Form {...form}>
      <div className="tw:flex tw:flex-col tw:gap-4">
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="tw:grid tw:grid-cols-12 tw:items-baseline tw:gap-4"
        >
          <div className="tw:col-span-12 tw:md:col-span-6">
            <FormField
              control={form.control}
              name="phone"
              rules={{ required: "Phone is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone number</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter phone number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="tw:col-span-12 tw:md:col-span-6">
            <FormField
              control={form.control}
              name="phone_type"
              rules={{ required: "Phone number type is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl className="tw:w-full">
                      <SelectTrigger>
                        <SelectValue placeholder="Select Phone Number Type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {getPhoneNumberTypesQuery.data?.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="tw:col-span-12 tw:flex tw:justify-end tw:gap-4">
            <Button
              type="submit"
              disabled={
                setProspectPhoneNumberMutation.isPending ||
                !form.formState.isDirty ||
                !form.formState.isValid
              }
            >
              {setProspectPhoneNumberMutation.isPending ? (
                <>
                  <Loader2 className="tw:animate-spin" />
                  <span>Adding...</span>
                </>
              ) : (
                <span>Add Phone Number</span>
              )}
            </Button>
          </div>
        </form>
        <DataTable
          pending={getProspectPhoneNumbersQuery.isPending}
          columns={prospectPhoneNumbersColumns(account_id)}
          data={getProspectPhoneNumbersQuery.data ?? []}
        />
      </div>
    </Form>
  )
}
